// Small pieces the promises list, its drawer and the CTP dialog share (spec 03
// §4.9): the status and path chips, the product's name, and the basis as a
// checklist with each assumption's last verdict.

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { StatusChip } from "../../../../components/status-chip";
import { formatTimestamp } from "../../../../lib/datetime";
import { useCalendarWorkCenters } from "../hooks/calendar-queries";
import { useProductIndex, useUoms, type Product } from "../hooks/planning-queries";
import type {
  FulfillmentPath,
  PromiseBasis,
  PromiseBasisVerdict,
  PromiseStatus,
} from "../hooks/promise-queries";
import { workCenterLabel } from "../lib/calendar-lib";
import { formatDate } from "../lib/planning-lib";
import { basisItems, promiseStatusTone, type BasisItem } from "../lib/promise-lib";
import { ExactQty } from "./mrp-bits";

export function PromiseStatusChip({ status }: { status: PromiseStatus }) {
  const { t } = useTranslation();
  const muted = status === "kept" || status === "withdrawn" || status === "expired";
  return (
    <StatusChip
      variant={promiseStatusTone(status)}
      dot={!muted}
      data-testid="planning-promise-status"
      data-status={status}
    >
      {t(`planning.promises.status.${status}`)}
    </StatusChip>
  );
}

export function PromisePathChip({ path }: { path: FulfillmentPath }) {
  const { t } = useTranslation();
  return (
    <span className="planning-chips" data-testid="planning-promise-path" data-path={path}>
      <StatusChip variant="neutral" dot={false}>
        {t(`planning.promises.path.${path}`)}
      </StatusChip>
    </span>
  );
}

/** "SKU · name" from the promise's own label, else from module 01's card. */
export function promiseProductText(
  label: string | null,
  product: Product | undefined,
): string | undefined {
  if (label !== null && label.length > 0) return label;
  if (product !== undefined) return `${product.sku} · ${product.name}`;
  return undefined;
}

/** A quantity in the product's base unit (§4.9: quantities are in the base unit). */
export function BaseQty({
  qty,
  product,
  testId,
}: {
  qty: string;
  product: Product | undefined;
  testId?: string;
}) {
  const uoms = useUoms();
  const unit =
    product === undefined
      ? undefined
      : uoms.data?.items.find((u) => u.id === product.base_uom_id)?.code;
  return <ExactQty qty={qty} unit={unit} {...(testId !== undefined ? { testId } : {})} />;
}

function Mark({ holds }: { holds: boolean | null }) {
  const { t } = useTranslation();
  if (holds === null) {
    return (
      <StatusChip variant="neutral" dot={false} className="planning-basis-mark">
        {t("planning.promises.basis.unchecked")}
      </StatusChip>
    );
  }
  return holds ? (
    <StatusChip variant="ok" className="planning-basis-mark">
      {`✓ ${t("planning.promises.basis.holds")}`}
    </StatusChip>
  ) : (
    <StatusChip variant="bad" className="planning-basis-mark">
      {`✗ ${t("planning.promises.basis.broken")}`}
    </StatusChip>
  );
}

/**
 * §4.9's checklist: each assumption the date rests on, with ✓ when the watch
 * last found it holding, ✗ when it did not, and a neutral mark before the
 * watch has checked it (a quote is never watched). `showMarks` is false in the
 * dialog, where nothing has been checked yet and a column of "not checked"
 * says nothing.
 */
export function BasisChecklist({
  basis,
  verdicts,
  facilityId,
  timezone,
  showMarks = true,
  horizonDays,
}: {
  basis: PromiseBasis;
  verdicts: readonly PromiseBasisVerdict[] | null;
  facilityId: string;
  /** The facility's zone, so a window reads in the plant's own time (D-409). */
  timezone?: string | null | undefined;
  showMarks?: boolean;
  horizonDays?: number | undefined;
}) {
  const { t, i18n } = useTranslation();
  const items = basisItems(basis, verdicts);
  const productIds = items.flatMap((i) => (i.productId !== undefined ? [i.productId] : []));
  const products = useProductIndex(productIds);
  const workCenters = useCalendarWorkCenters(
    items.some((i) => i.kind === "capacity_window") ? facilityId : "",
  );
  const lang = i18n.language;

  const text = (item: BasisItem): ReactNode => {
    switch (item.kind) {
      case "offcut_unit":
        return t("planning.promises.basis.offcut", { length: item.lengthM ?? "" });
      case "stock":
        return t("planning.promises.basis.stock", { qty: item.qty ?? "" });
      case "in_transit":
        return t("planning.promises.basis.inTransit", { qty: item.qty ?? "" });
      case "supply":
        return item.supplyKind === "planned_order"
          ? t("planning.promises.basis.plannedOrder")
          : t("planning.promises.basis.workOrder");
      case "capacity_window": {
        const wc = workCenters.data?.find((w) => w.id === item.workCenterId);
        return t("planning.promises.basis.window", {
          workCenter:
            wc !== undefined ? workCenterLabel(wc, lang) : t("planning.promises.basis.workCenter"),
          from:
            item.windowStart !== undefined ? formatTimestamp(item.windowStart, lang, timezone) : "",
          to: item.windowEnd !== undefined ? formatTimestamp(item.windowEnd, lang, timezone) : "",
        });
      }
      case "material": {
        const product =
          item.productId !== undefined ? products.byId.get(item.productId) : undefined;
        return t(`planning.promises.basis.material.${item.source ?? "on_hand"}`, {
          product:
            product !== undefined
              ? `${product.sku} · ${product.name}`
              : t("planning.promises.columns.product"),
          date: item.availableDate !== undefined ? formatDate(item.availableDate, lang) : "",
        });
      }
    }
  };

  return (
    <div className="planning-basis" data-testid="planning-promise-basis">
      {items.length === 0 ? (
        <p className="planning-note">{t("planning.promises.basis.none")}</p>
      ) : (
        <ul className="planning-basis-list">
          {items.map((item) => (
            <li
              key={item.key}
              className="planning-basis-item"
              data-testid="planning-promise-basis-item"
              data-kind={item.kind}
              data-ref={item.ref}
              data-holds={item.holds === null ? "unchecked" : String(item.holds)}
            >
              <span>{text(item)}</span>
              {showMarks ? <Mark holds={item.holds} /> : null}
            </li>
          ))}
        </ul>
      )}
      <p className="planning-note" data-testid="planning-promise-margin">
        {t("planning.promises.basis.margin", { count: basis.safety_margin_days })}
      </p>
      {basis.capacity_beyond_horizon ? (
        <p className="planning-note" data-testid="planning-promise-beyond-horizon">
          {horizonDays !== undefined
            ? t("planning.promises.basis.beyondHorizon", { days: horizonDays })
            : t("planning.promises.basis.beyondHorizonShort")}
        </p>
      ) : null}
    </div>
  );
}
